/**
 * ธีม "พื้นหลังไหล" (Ground) — ฝั่ง DOM: ตั้งสี/โทนบน <html> + วาดพื้นหลัง WebGL ด้านหลังทั้งหน้า
 * logic ล้วนอยู่ใน groundCore.ts · ใช้เฉพาะเมื่อ <html data-tone="ground"> (แอดมินเลือก palette = ground)
 *
 * ต้นทุนเครื่องลูกค้า: วาดไม่เกิน ~30 เฟรม/วินาที · หยุดเมื่อแท็บถูกซ่อน/ไม่อยู่ในจอ ·
 * โหมดประหยัดพลังงาน (data-energy="on") หรือเปิดลดการเคลื่อนไหว = วาดภาพนิ่ง 1 เฟรม · WebGL2 ไม่ได้ = เห็นแค่สีพื้น+ม่าน
 */
import {
  GROUND_BOOT_KEY,
  GROUND_STORAGE_KEY,
  parseGroundPreset,
  groundFromRestDoc,
  normalizeGroundConfig,
  parseStoredGround,
  resolveGround,
  rgb01,
  veilCss,
  type GroundConfig,
  type GroundResolved,
} from './groundCore'

const GROUND_ID = 'oc-ground'
const FRAME_MS = 1000 / 30

const VS = '#version 300 es\nlayout(location=0) in vec2 aPos;void main(){gl_Position=vec4(aPos,0.0,1.0);}'
/** สูตรเดียวกับ HyperFrames: บิดพิกัด 6 รอบ → คลื่น sin แยกช่อง RGB → ยกกำลังให้เหลือยอดคลื่น · mono = เส้นสีเดียว */
const FS =
  '#version 300 es\nprecision highp float;out vec4 fragColor;uniform vec2 uRes;uniform float uTime;uniform float uColorScale;uniform float uFrequency;uniform vec3 uBaseColor;uniform vec3 uTint;uniform float uMode;' +
  'vec2 distort(vec2 p,float o){p+=o;for(float i=1.0;i<7.0;i++){p.x+=0.3/i*sin(i*5.0*p.y+uTime);p.y+=0.1/i*cos(i*3.0*p.x+uTime);}return p;}' +
  'void main(){vec2 uv=gl_FragCoord.xy/uRes.xy;' +
  'float r=sin(distort(uv,0.0).x*uFrequency)*0.5+0.5;float g=sin(distort(uv,0.02).x*uFrequency)*0.5+0.5;float b=sin(distort(uv,0.04).x*uFrequency)*0.5+0.5;' +
  'vec3 rb=mix(uBaseColor,pow(vec3(r,g,b),vec3(uColorScale)),0.8);' +
  'float v=pow(r,uColorScale*0.6);vec3 mo=mix(uBaseColor,uTint,v*0.9);' +
  'fragColor=vec4(uMode<0.5?rb:mo,1.0);}'

interface Painter {
  setColors(r: GroundResolved): void
  frame(): void
  dispose(): void
}

function createPainter(canvas: HTMLCanvasElement): Painter | null {
  const gl = canvas.getContext('webgl2', { alpha: false, antialias: false, depth: false, stencil: false })
  if (!gl) return null
  const compile = (type: number, src: string) => {
    const s = gl.createShader(type)
    if (!s) throw new Error('shader')
    gl.shaderSource(s, src)
    gl.compileShader(s)
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) ?? 'compile')
    return s
  }
  let program: WebGLProgram | null = null
  let buffer: WebGLBuffer | null = null
  try {
    program = gl.createProgram()
    if (!program) return null
    gl.attachShader(program, compile(gl.VERTEX_SHADER, VS))
    gl.attachShader(program, compile(gl.FRAGMENT_SHADER, FS))
    gl.bindAttribLocation(program, 0, 'aPos')
    gl.linkProgram(program)
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) throw new Error('link')
    buffer = gl.createBuffer()
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)
  } catch {
    if (program) gl.deleteProgram(program)
    return null
  }
  const prog = program
  const u = (n: string) => gl.getUniformLocation(prog, n)
  const U = { res: u('uRes'), time: u('uTime'), scale: u('uColorScale'), freq: u('uFrequency'), base: u('uBaseColor'), tint: u('uTint'), mode: u('uMode') }
  let colors: GroundResolved | null = null
  let t = Math.random() * 20
  let last = performance.now()
  return {
    setColors(r) {
      colors = r
    },
    frame() {
      if (!colors) return
      const now = performance.now()
      t += 0.00375 * Math.min(0.05, (now - last) / 1000) * 60
      last = now
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5)
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr))
      const h = Math.max(1, Math.round(canvas.clientHeight * dpr))
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w
        canvas.height = h
      }
      gl.viewport(0, 0, w, h)
      gl.useProgram(prog)
      gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
      gl.enableVertexAttribArray(0)
      gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
      const b = rgb01(colors.base)
      const tn = rgb01(colors.tint)
      gl.uniform2f(U.res, w, h)
      gl.uniform1f(U.time, t)
      gl.uniform1f(U.scale, 10)
      gl.uniform1f(U.freq, 7)
      gl.uniform3f(U.base, b[0], b[1], b[2])
      gl.uniform3f(U.tint, tn[0], tn[1], tn[2])
      gl.uniform1f(U.mode, colors.mode === 'mono' ? 1 : 0)
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    },
    dispose() {
      gl.deleteBuffer(buffer)
      gl.deleteProgram(prog)
    },
  }
}

let painter: Painter | null = null
let host: HTMLDivElement | null = null
let raf = 0
let lastTick = 0
let resolved: GroundResolved | null = null

function motionStill(): boolean {
  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return true
  } catch {
    /* ไม่รองรับ matchMedia */
  }
  return document.documentElement.dataset.energy === 'on'
}

function stopLoop() {
  if (raf) cancelAnimationFrame(raf)
  raf = 0
}

function tick(now: number) {
  raf = 0
  if (!painter || !host) return
  if (document.hidden || motionStill()) return // กลับมาแล้วเริ่มใหม่ผ่าน visibilitychange
  if (now - lastTick >= FRAME_MS) {
    lastTick = now
    painter.frame()
  }
  raf = requestAnimationFrame(tick)
}

function startLoop() {
  if (!painter) return
  painter.frame() // อย่างน้อย 1 เฟรมเสมอ (ภาพนิ่งเมื่อประหยัดพลังงาน)
  if (!raf && !document.hidden && !motionStill()) raf = requestAnimationFrame(tick)
}

function onVisibility() {
  if (document.hidden) stopLoop()
  else startLoop()
}
function onResize() {
  painter?.frame()
}

function mount() {
  if (host) return
  host = document.createElement('div')
  host.id = GROUND_ID
  host.setAttribute('aria-hidden', 'true')
  host.innerHTML = '<canvas class="oc-ground__gl"></canvas><div class="oc-ground__veil"></div>'
  document.body.insertBefore(host, document.body.firstChild)
  painter = createPainter(host.querySelector('canvas') as HTMLCanvasElement)
  document.addEventListener('visibilitychange', onVisibility)
  window.addEventListener('resize', onResize)
}

function unmount() {
  stopLoop()
  document.removeEventListener('visibilitychange', onVisibility)
  window.removeEventListener('resize', onResize)
  painter?.dispose()
  painter = null
  host?.remove()
  host = null
}

export function readStoredGround(): GroundConfig {
  let cfg: GroundConfig
  try {
    cfg = parseStoredGround(localStorage.getItem(GROUND_STORAGE_KEY))
  } catch {
    cfg = normalizeGroundConfig(null)
  }
  // ?ground=temple|velvet|hf|champagne|custom = ทดสอบ (ไม่บันทึก)
  try {
    const p = parseGroundPreset(new URLSearchParams(window.location.search).get('ground'))
    if (p) cfg = { ...cfg, preset: p }
  } catch {
    /* ไม่มี URLSearchParams */
  }
  return cfg
}

/** ใส่สี/โทนบน <html> + อัปเดตพื้นหลัง · เรียกซ้ำได้ตอนค่าเปลี่ยน */
export function applyGround(config: GroundConfig): void {
  const r = resolveGround(config)
  resolved = r
  const root = document.documentElement
  root.dataset.groundTone = r.tone
  root.dataset.scheme = r.tone === 'dark' ? 'dark' : 'light' // โทนถูกกำหนดโดยธีม ไม่ตามเวลา/เครื่อง
  root.style.setProperty('--gr-base', r.base)
  root.style.setProperty('--gr-gold', r.gold)
  root.style.setProperty('--gr-veil', veilCss(r))
  try {
    localStorage.setItem(GROUND_BOOT_KEY, `${r.tone}|${r.base}`) // inline script ใน index.html อ่านก่อนวาดหน้า กันกะพริบ
  } catch {
    /* private mode / quota */
  }
  mount()
  painter?.setColors(r)
  if (host) (host.querySelector('.oc-ground__veil') as HTMLElement).style.background = veilCss(r)
  stopLoop()
  startLoop()
}

/** ออกจากธีม Ground — เก็บกวาด DOM/ตัวแปร (data-scheme ให้ผู้เรียกคืนค่าอัตโนมัติ) */
export function clearGround(): void {
  resolved = null
  unmount()
  const root = document.documentElement
  delete root.dataset.groundTone
  ;['--gr-base', '--gr-gold', '--gr-veil'].forEach((k) => root.style.removeProperty(k))
}

export function isGroundActive(): boolean {
  return resolved !== null
}

/** เก็บ ground จาก doc แอดมินลง localStorage (เปิดครั้งหน้าใช้ได้ก่อนโหลดเน็ต) — คืนค่าที่อ่านได้ */
export function groundFromDocAndStore(doc: unknown): GroundConfig {
  const cfg = groundFromRestDoc(doc)
  try {
    localStorage.setItem(GROUND_STORAGE_KEY, JSON.stringify(cfg))
  } catch {
    /* private mode / quota */
  }
  return cfg
}
